import { makeAutoObservable, runInAction } from 'mobx';
import type RootStore from '../RootStore';

export class WakeLockStore {
    rootStore: RootStore;
    isSupported = 'wakeLock' in navigator;
    wakeLock: WakeLockSentinel | null = null;
    isActive = false;

    constructor(rootStore: RootStore) {
        makeAutoObservable(this, {
            rootStore: false,
            wakeLock: false,
        });
        this.rootStore = rootStore;

        // Re-acquire wake lock on visibility change
        if (this.isSupported) {
            document.addEventListener('visibilitychange', this.handleVisibilityChange);
        }
    }

    private handleVisibilityChange = async () => {
        if (this.isActive && document.visibilityState === 'visible' && !this.wakeLock) {
            await this.request();
        }
    };

    async request(): Promise<void> {
        if (!this.isSupported) {
            throw new Error('Wake Lock API not supported');
        }

        try {
            this.wakeLock = await navigator.wakeLock.request('screen');

            this.wakeLock.addEventListener('release', () => {
                runInAction(() => {
                    this.wakeLock = null;
                    this.isActive = false;
                });
            });

            runInAction(() => {
                this.isActive = true;
            });
        } catch (error: any) {
            console.error('Failed to acquire wake lock:', error);
            throw error;
        }
    }

    async release(): Promise<void> {
        if (this.wakeLock) {
            await this.wakeLock.release();
            runInAction(() => {
                this.wakeLock = null;
                this.isActive = false;
            });
        }
    }

    dispose() {
        this.release();
        if (this.isSupported) {
            document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        }
    }
}